import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  isSacredAdminMode,
  setSacredAdminMode,
  loginSacredEditor,
  getEditorAuth,
  setEditorAuth,
  fetchBootstrapStatus,
  fetchSacredEditorMe,
} from "@/lib/sacred-library/sacred-library-api";
import { useSacredAdmin } from "@/hooks/useSacredAdmin";
import { UserManagement } from "./UserManagement";
import { ApprovalQueue } from "./ApprovalQueue";
import { PagesAdmin } from "./PagesAdmin";
import { TattvaGraphAdmin } from "./TattvaGraphAdmin";
import { NotificationsAdmin } from "./NotificationsAdmin";
import { JourneyEngineAdmin } from "./JourneyEngineAdmin";
import { BootstrapSetup } from "./BootstrapSetup";
import { TownhallAdmin } from "./TownhallAdmin";
import { Users, CheckSquare, BookOpen, FileText, Network, Bell, Compass, Building2 } from "lucide-react";

export function AdminPanel() {
  const { refresh, editorUser, isAdmin, canEditPages, canEditLibrary, isStaff } = useSacredAdmin();
  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("contact.vedapath@gmail.com");
  const [password, setPassword] = useState("");
  const [adminOn, setAdminOn] = useState(isSacredAdminMode());
  const [needsBootstrap, setNeedsBootstrap] = useState(null);
  const [loginError, setLoginError] = useState("");

  useEffect(() => {
    if (isStaff && tab === "login") {
      setTab(isAdmin ? "users" : canEditPages ? "pages" : "library");
    }
  }, [isStaff, isAdmin, canEditPages, tab]);

  useEffect(() => {
    fetchBootstrapStatus()
      .then((d) => setNeedsBootstrap(d.needs_bootstrap))
      .catch(() => setNeedsBootstrap(null));
  }, [editorUser]);

  useEffect(() => {
    if (!getEditorAuth().token) return;
    fetchSacredEditorMe()
      .then(() => refresh())
      .catch(() => {});
  }, []);

  const onLogin = async () => {
    setLoginError("");
    try {
      const data = await loginSacredEditor(email, password);
      await fetchSacredEditorMe().catch(() => {});
      refresh();
      const role = getEditorAuth().user?.role || data.user?.role;
      setTab(role === "admin" ? "users" : role === "staff" ? "pages" : "library");
      setNeedsBootstrap(false);
    } catch (e) {
      setLoginError(e?.response?.data?.detail || "Invalid credentials — run bootstrap if this is first setup.");
    }
  };

  const toggleAdminMode = () => {
    const next = !adminOn;
    setSacredAdminMode(next);
    setAdminOn(next);
    refresh();
  };

  const logout = () => {
    setEditorAuth(null, null);
    refresh();
    setTab("login");
  };

  const notLoggedIn = !isStaff;

  return (
    <div className="min-h-screen py-24 px-4 bg-background text-foreground" data-testid="sacred-admin-panel">
      <div className="max-w-4xl mx-auto">
        <h1 className="font-heading text-3xl mb-2 text-foreground">VedaPath Content Admin</h1>
        <p className="text-muted-foreground mb-6">Sacred Library topics, universal site pages (CMS), users, and approvals.</p>

        <Card className="mb-6">
          <CardContent className="pt-6 flex flex-wrap gap-4 items-end">
            <div>
              <Label className="text-xs">Admin mode (localStorage vedapath_admin)</Label>
              <Button variant={adminOn ? "default" : "outline"} onClick={toggleAdminMode} className="mt-1">
                {adminOn ? "ON" : "OFF"}
              </Button>
            </div>
            {editorUser && (
              <p className="text-sm">
                Signed in as <strong>{editorUser.email}</strong> ({editorUser.role})
                <Button variant="link" size="sm" onClick={logout}>
                  Logout
                </Button>
              </p>
            )}
          </CardContent>
        </Card>

        {notLoggedIn && (
          <BootstrapSetup
            onSuccess={() =>
              fetchBootstrapStatus().then((d) => setNeedsBootstrap(d.needs_bootstrap))
            }
          />
        )}

        {notLoggedIn ? (
          <Card>
            <CardHeader>
              <CardTitle>Editor login</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 max-w-md">
              {needsBootstrap && (
                <p className="text-xs text-amber-700">
                  Complete <strong>One-time login setup</strong> above first.
                </p>
              )}
              <div>
                <Label>Email</Label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div>
                <Label>Password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              {loginError && <p className="text-sm text-destructive">{loginError}</p>}
              <Button onClick={onLogin} data-testid="sacred-admin-login">
                Sign in
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-6">
              {canEditLibrary && (
                <Button variant={tab === "library" ? "default" : "outline"} size="sm" onClick={() => setTab("library")}>
                  <BookOpen className="w-4 h-4 mr-1" /> Library
                </Button>
              )}
              {canEditPages && (
                <Button variant={tab === "pages" ? "default" : "outline"} size="sm" onClick={() => setTab("pages")}>
                  <FileText className="w-4 h-4 mr-1" /> Pages
                </Button>
              )}
              {canEditLibrary && (
                <Button variant={tab === "tattva" ? "default" : "outline"} size="sm" onClick={() => setTab("tattva")}>
                  <Network className="w-4 h-4 mr-1" /> Tattva Graph
                </Button>
              )}
              {canEditLibrary && (
                <Button variant={tab === "townhall" ? "default" : "outline"} size="sm" onClick={() => setTab("townhall")}>
                  <Building2 className="w-4 h-4 mr-1" /> Townhall
                </Button>
              )}
              {isAdmin && adminOn && (
                <>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/admin/tattva-test">Tattva QA</Link>
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/admin/alerts-test">Alerts QA</Link>
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/admin/journey-test">Journey QA</Link>
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/admin/journey-validation">Journey Validation</Link>
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/guide">Acharya 3.0</Link>
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/admin/acharya-test">Acharya Test</Link>
                  </Button>
                </>
              )}
              {isAdmin && adminOn && (
                <>
                  <Button variant={tab === "users" ? "default" : "outline"} size="sm" onClick={() => setTab("users")}>
                    <Users className="w-4 h-4 mr-1" /> Users
                  </Button>
                  <Button variant={tab === "approvals" ? "default" : "outline"} size="sm" onClick={() => setTab("approvals")}>
                    <CheckSquare className="w-4 h-4 mr-1" /> Approvals
                  </Button>
                  <Button variant={tab === "notifications" ? "default" : "outline"} size="sm" onClick={() => setTab("notifications")}>
                    <Bell className="w-4 h-4 mr-1" /> Notifications
                  </Button>
                  <Button variant={tab === "journey" ? "default" : "outline"} size="sm" onClick={() => setTab("journey")}>
                    <Compass className="w-4 h-4 mr-1" /> Journey Engine
                  </Button>
                </>
              )}
            </div>
            {tab === "library" && canEditLibrary && (
              <Card>
                <CardContent className="pt-6">
                  <Link to="/library" className="text-primary hover:underline">
                    Open Sacred Library →
                  </Link>
                  <p className="text-sm text-muted-foreground mt-2">
                    Enable Admin mode, then use Edit on any topic page.
                  </p>
                </CardContent>
              </Card>
            )}
            {tab === "pages" && canEditPages && <PagesAdmin />}
            {tab === "tattva" && canEditLibrary && <TattvaGraphAdmin />}
            {tab === "townhall" && canEditLibrary && <TownhallAdmin />}
            {tab === "users" && isAdmin && adminOn && <UserManagement isAdmin={isAdmin} />}
            {tab === "approvals" && isAdmin && adminOn && <ApprovalQueue />}
            {tab === "notifications" && isAdmin && adminOn && <NotificationsAdmin />}
            {tab === "journey" && isAdmin && adminOn && <JourneyEngineAdmin />}
            {tab === "users" && isAdmin && !adminOn && (
              <p className="text-sm text-muted-foreground">Turn on Admin mode to manage users.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
